// src/components/pages/Pessoa/modules/usePessoaEnderecoModule.js

import { useCallback, useEffect, useMemo, useState } from 'react';
import * as PessoaAPI from '../../../../api/pessoa';
import * as VerificadoresAPI from '../../../../api/verificadores';

function createEnderecoDraft(pessoaId = null) {
    return {
        pessoaId: pessoaId || null,
        uuid: '',
        cep: '',
        latitude: null,
        longitude: null,
        ufId: '',
        municipioId: '',
        bairro: '',
        logradouro: '',
        numero: '',
        quadra: '',
        complemento: '',
        enderecoBancarioSimplificado: '',
        tipo: 'Comercial',
        principal: false,
        ativo: false,
        funcao: 'Adicionar',
    };
}

export function usePessoaEnderecoModule({ pessoaId, rows = [], setRows, setFlashMessage }) {
    const [draft, setDraft] = useState(createEnderecoDraft(pessoaId));
    const [editingIndex, setEditingIndex] = useState(null);
    const [optionsMunicipio, setOptionsMunicipio] = useState([]);

    useEffect(() => {
        setDraft((prev) => ({
            ...prev,
            pessoaId: pessoaId || null,
        }));
    }, [pessoaId]);

    const carregarMunicipios = useCallback(async (ufId) => {
        if (!ufId) {
            setOptionsMunicipio([]);
            return;
        }

        try {
            const cidades = await VerificadoresAPI.municipio(ufId, '', '');
            setOptionsMunicipio(cidades || []);
        } catch (error) {
            console.error(error);
            setOptionsMunicipio([]);
        }
    }, []);
    const onlyNumbers = (str = '') => String(str).replace(/[^0-9]/g, '');

    const consultarCep = useCallback(
        async (cep) => {
            const cepLimpo = onlyNumbers(cep);

            if (cepLimpo.length !== 8) return;

            try {
                const result = await VerificadoresAPI.cep(cepLimpo);

                if (result?.type === 'error') {
                    if (typeof setFlashMessage === 'function') {
                        setFlashMessage(result.message, result.type);
                    }
                    return;
                }

                if (result?.ufId) {
                    await carregarMunicipios(result.ufId);
                }

                setDraft((prev) => ({
                    ...prev,
                    cep: cepLimpo,
                    ufId: result?.ufId || prev.ufId || '',
                    municipioId: result?.municipioId || prev.municipioId || '',
                    bairro: result?.neighborhood || prev.bairro || '',
                    logradouro: result?.street || prev.logradouro || '',
                }));
            } catch (error) {
                console.error(error);
                if (typeof setFlashMessage === 'function') {
                    setFlashMessage('Erro ao consultar CEP.', 'error');
                }
            }
        },
        [carregarMunicipios, setFlashMessage]
    );
    const resetDraft = useCallback(() => {
        setDraft(createEnderecoDraft(pessoaId));
        setEditingIndex(null);
        setOptionsMunicipio([]);
    }, [pessoaId]);

    const handleEdit = useCallback(
        async (row, index) => {
            setDraft({
                index,
                pessoaId: row.pessoaId || pessoaId || null,
                uuid: row.uuid || '',
                cep: row.cep || '',
                latitude: row.latitude ?? null,
                longitude: row.longitude ?? null,
                ufId: row.ufId || '',
                municipioId: row.municipioId || '',
                bairro: row.bairro || '',
                logradouro: row.logradouro || '',
                numero: row.numero || '',
                quadra: row.quadra || '',
                complemento: row.complemento || '',
                enderecoBancarioSimplificado: row.enderecoBancarioSimplificado ?? '',
                tipo: row.tipo || 'Comercial',
                principal: !!row.principal,
                ativo: !!row.ativo,
                funcao: 'Editar',
            });

            setEditingIndex(index);

            if (row.ufId) {
                await carregarMunicipios(row.ufId);
            } else {
                setOptionsMunicipio([]);
            }
        },
        [pessoaId, carregarMunicipios]
    );

    const handleDelete = useCallback(
        async (row, index) => {
            const payload = {
                index,
                pessoaId: row.pessoaId || pessoaId || null,
                uuid: row.uuid || '',
                cep: row.cep || '',
                latitude: row.latitude ?? null,
                longitude: row.longitude ?? null,
                ufId: row.ufId || '',
                municipioId: row.municipioId || '',
                bairro: row.bairro || '',
                logradouro: row.logradouro || '',
                numero: row.numero || '',
                quadra: row.quadra || '',
                complemento: row.complemento || '',
                tipo: row.tipo || 'Comercial',
                principal: !!row.principal,
                ativo: !!row.ativo,
                funcao: 'deletar',
            };

            if (!pessoaId) {
                setRows((prev) => prev.filter((_, i) => i !== index));
                setFlashMessage?.('Endereço removido da lista.', 'success');

                if (editingIndex === index) {
                    resetDraft();
                }
                return;
            }

            try {
                const result = await PessoaAPI.enderecos(payload);

                if (result?.data?.type === 'success') {
                    setRows(result.data.resultado || []);
                }

                setFlashMessage?.(result?.data?.message || 'Operação realizada.', result?.data?.type || 'success');

                if (editingIndex === index) {
                    resetDraft();
                }
            } catch (error) {
                console.error(error);
                setFlashMessage?.(error?.response?.data?.message || 'Erro ao excluir endereço.', 'error');
            }
        },
        [pessoaId, setRows, setFlashMessage, editingIndex, resetDraft]
    );

    const handleSave = useCallback(async () => {
        if (!draft.cep?.trim()) {
            setFlashMessage?.('Digite o CEP.', 'warning');
            return;
        }

        const simplificado = draft.enderecoBancarioSimplificado ?? '';
        if (
            typeof simplificado !== 'string' ||
            [...simplificado].length > 40 ||
            /[\u0000-\u001f\u007f-\u009f]/u.test(simplificado)
        ) {
            setFlashMessage?.('Informe o endereço bancário simplificado com até 40 caracteres, em uma única linha.', 'warning');
            return;
        }

        const payload = {
            ...draft,
            enderecoBancarioSimplificado: simplificado.trim() === '' ? '' : simplificado,
            pessoaId: pessoaId || draft.pessoaId || null,
        };

        if (!pessoaId) {
            if (String(payload.funcao).toLowerCase() === 'editar' && editingIndex !== null) {
                setRows((prev) =>
                    prev.map((item, index) =>
                        index === editingIndex
                            ? {
                                  ...item,
                                  ...payload,
                              }
                            : item
                    )
                );
                setFlashMessage?.('Endereço atualizado na lista.', 'success');
            } else {
                setRows((prev) => [
                    ...prev,
                    {
                        ...payload,
                        uuid: payload.uuid || `draft-end-${Date.now()}`,
                    },
                ]);
                setFlashMessage?.('Endereço adicionado na lista.', 'success');
            }

            resetDraft();
            return;
        }

        try {
            const result = await PessoaAPI.enderecos(payload);

            if (result?.data?.type === 'success') {
                setRows(result.data.resultado || []);
            }

            setFlashMessage?.(result?.data?.message || 'Não foi possível confirmar a gravação.', result?.data?.type || 'warning');
            if (result?.data?.type === 'success') resetDraft();
        } catch (error) {
            console.error(error);
            setFlashMessage?.(error?.response?.data?.message || 'Erro ao salvar endereço.', 'error');
        }
    }, [draft, pessoaId, editingIndex, resetDraft, setRows, setFlashMessage]);

    const columns = useMemo(
        () => [
            {
                id: 'endereco',
                header: 'Endereços da pessoa',
                columns: [
                    {
                        accessorKey: 'cep',
                        header: 'CEP',
                        size: 110,
                    },
                    {
                        accessorKey: 'logradouro',
                        header: 'Logradouro',
                        size: 220,
                    },
                    {
                        accessorKey: 'numero',
                        header: 'Número',
                        size: 90,
                    },
                    {
                        accessorKey: 'bairro',
                        header: 'Bairro',
                        size: 160,
                    },
                    {
                        accessorFn: (row) => row.Municipio?.descricao || '',
                        accessorKey: 'Municipio.descricao',
                        header: 'Município',
                        size: 160,
                    },
                    {
                        accessorFn: (row) => row.UF?.uf || '',
                        accessorKey: 'UF.uf',
                        header: 'UF',
                        size: 70,
                    },
                    {
                        accessorKey: 'enderecoBancarioSimplificado',
                        header: 'Endereço bancário simplificado',
                        size: 260,
                    },
                    {
                        accessorKey: 'tipo',
                        header: 'Tipo',
                        size: 120,
                    },
                    {
                        accessorKey: 'principal',
                        header: 'Principal',
                        size: 90,
                        Cell: ({ row }) => (row.original.principal ? 'Sim' : 'Não'),
                    },
                    {
                        accessorKey: 'ativo',
                        header: 'Ativo',
                        size: 80,
                        Cell: ({ row }) => (row.original.ativo ? 'Sim' : 'Não'),
                    },
                ],
            },
        ],
        []
    );

    return {
        state: {
            items: rows || [],
            rowCount: rows?.length || 0,
            isLoading: false,
            isRefetching: false,
            isError: false,
            novoEndereco: draft,
            optionsMunicipio,
        },
        actions: {
            setNovoEndereco: setDraft,
            limpar: resetDraft,
            submit: handleSave,
            handleEditarOuDeletar: (row, index, funcao) => {
                if (String(funcao).toLowerCase() === 'editar') {
                    handleEdit(row, index);
                    return;
                }
                handleDelete(row, index);
            },
            change: async (e) => {
                const { name, value, checked, type, options, selectedIndex } = e.target;
                const field = name.split('.')[1];

                if (field === 'tipo') {
                    setDraft((prev) => ({
                        ...prev,
                        tipo: options?.[selectedIndex]?.text || value,
                    }));
                    return;
                }

                if (field === 'principal' || field === 'ativo') {
                    setDraft((prev) => ({
                        ...prev,
                        [field]: checked,
                    }));
                    return;
                }

                if (field === 'ufId') {
                    setDraft((prev) => ({
                        ...prev,
                        ufId: value,
                        municipioId: '',
                    }));
                    await carregarMunicipios(value);
                    return;
                }

                setDraft((prev) => ({
                    ...prev,
                    [field]: type === 'checkbox' ? checked : value,
                }));
            },
            focus: () => {},
            blur: async (e) => {
                const { name, value } = e.target;
                const field = name.split('.')[1];

                if (field === 'cep') {
                    const cepLimpo = onlyNumbers(value);

                    if (!cepLimpo) return;

                    if (cepLimpo.length !== 8) {
                        if (typeof setFlashMessage === 'function') {
                            setFlashMessage('CEP inválido!', 'error');
                        }
                        return;
                    }

                    await consultarCep(cepLimpo);
                }
            },
        },
        columns,
    };
}
